// ── Companies ("/companies") — the company directory (Wave 1) ──────────────
// ONE honest read (the companies list) routed through useHonestQuery ->
// <HonestState>. The page NEVER hand-rolls state branching: a gate /
// permission-denied / unavailable / error response renders via <HonestState>
// (tagged data-honest-posture), NEVER as a plain empty table. Real content (the
// DataTable + filter/create toolbar) renders only on the ready-with-data (or
// honest-empty) path.
//
// ROLE-GATING (presentational; the server re-checks and 403s anyway):
//  - Super Admin / Admin -> Create + edit + archive affordances.
//  - Company Admin / Coach -> read-only (the preserved route manifest grants
//    them READ via operationsRoles; the denied write capability hides every
//    create/edit/archive affordance, never an error).
//  - Cohort Lead / Participant -> the route is denied upstream (the route
//    manifest excludes them); a denied READ renders permission-denied, never empty.
//
// Affordances render ONLY when the read is READY (posture null) or honest-EMPTY
// (so filters can still be adjusted); a hard-denied read shows ONLY the honest
// state — no create/edit/archive controls hanging over a refused surface.

import { Button, CollectionToolbar, IconUsers, ProTable, useToast } from "@vjal/ui";
import { actionProps } from "@vjal/screen-kit";
import { useMemo, useState } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useOrganizationLifecycleMutations } from "../../hooks/useOrganizationLifecycleMutations";
import { evaluateActionCapability } from "../../domain/capabilities";
import type { CoachingCompanyDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useCompaniesCollection } from "./companies-collection";
import { CompanyFormModal, type CompanyFormValues } from "./company-form-modal";
import { CompanyArchiveModal } from "./company-archive-modal";
import { CoachingOrgOverview } from "./CoachingOrgOverview";
const services = getCoachingServices();

export function CompaniesPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useOrganizationLifecycleMutations();

  const allowCreate = evaluateActionCapability(role, "companies:create").allowed;
  const allowUpdate = evaluateActionCapability(role, "companies:update").allowed;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CoachingCompanyDto | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CoachingCompanyDto | null>(null);

  // ── The one honest read ─────────────────────────────────────────────────────
  const query = useHonestQuery<ListResponse<CoachingCompanyDto>>(
    coachingQueryKeys.list("companies.list"),
    () => services.companies.list()
  );
  const items = query.data?.items;
  const allRows = useMemo(() => items ?? [], [items]);
  const collectionActions = useMemo(
    () => ({
      allowUpdate,
      onEdit: setEditTarget,
      onArchive: setArchiveTarget
    }),
    [allowUpdate]
  );
  const collection = useCompaniesCollection(allRows, collectionActions, routePath);

  // useHonestQuery flags an empty `{items:[]}` as "empty"; once filters narrow a
  // non-empty list to zero we render the empty body ourselves (never a blank).
  const posture =
    query.posture === null && collection.total === 0 && allRows.length > 0 ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  // ── Writes (each toasts ONLY after the backend confirms; no false success) ──
  function submitCreate(values: CompanyFormValues) {
    return mutations.createCompany.mutateAsync({ name: values.name, status: values.status }).then((company) => {
      setCreateOpen(false);
      mutations.createCompany.reset();
      const name = company.name ?? values.name;
      toast.success(company.replayed
        ? "Company already exists — no duplicate created"
        : company.outcome === "reused"
          ? `Existing company ${name} reused`
          : company.outcome === "restored"
            ? `Existing company ${name} restored`
            : `Company ${name} created`);
      return company;
    });
  }

  function submitEdit(values: CompanyFormValues) {
    if (!editTarget) return Promise.reject(new Error("No company selected"));
    return mutations.updateCompany
      .mutateAsync({ companyId: editTarget.id, body: { name: values.name, status: values.status } })
      .then((company) => {
        setEditTarget(null);
        mutations.updateCompany.reset();
        toast.success(`Company ${company.name ?? values.name} updated`);
        return company;
      });
  }

  function submitArchive() {
    if (!archiveTarget) return Promise.reject(new Error("No company selected"));
    return mutations.updateCompany
      .mutateAsync({ companyId: archiveTarget.id, body: { name: archiveTarget.name, status: "archived" } })
      .then((company) => {
        setArchiveTarget(null);
        mutations.updateCompany.reset();
        toast.success(`Company ${company.name ?? archiveTarget.name} archived`);
        return company;
      });
  }

  return (
    <section
      aria-label="Companies"
      className="col-span-12 flex flex-col gap-4"
      data-testid="companies-page"
    >
      {role === "super_admin" ? <CoachingOrgOverview /> : null}
      {/* Empty/gated: keep honest posture + optional toolbar without fabricating rows. */}
      {showToolbar && gated ? (
        <div {...actionProps("filter")}>
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by name…"
            primaryAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add company
                </Button>
              ) : undefined
            }
            className="rounded-xl border border-line"
            stableChipNames
          />
        </div>
      ) : null}

      <div className="rounded-xl border border-line bg-white">
        {gated ? (
          <HonestState
            posture={posture ?? "loading"}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            icon={<IconUsers size={22} />}
            canCreate={allowCreate && !showToolbar}
            title={posture === "empty" ? "No companies yet" : undefined}
            message={
              posture === "empty"
                ? allowCreate
                  ? "Add the first company to start scoping programs and cohorts."
                  : "Companies will appear here once they are created."
                : undefined
            }
            createAction={
              allowCreate ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  Add company
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div {...actionProps("filter")}>
            <div {...actionProps("sort")}>
              <div {...actionProps("row-open")}>
                <div {...actionProps("paginate")}>
                  {/* F3: real @vjal/ui ProTable-1 recipe export (not marker-only table chrome). */}
                  <ProTable
                    collection={collection}
                    primaryAction={
                      allowCreate ? (
                        <Button variant="primary" onClick={() => setCreateOpen(true)}>
                          Add company
                        </Button>
                      ) : undefined
                    }
                    className="p-0"
                    tableProps={{
                      caption: "Companies in the current view",
                      emptyTitle: "No companies match your search",
                      emptyMessage: "Clear or change the filters above to widen the view.",
                      tableClassName: "min-w-[680px]",
                    }}
                    stableChipNames
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {allowCreate && (
        <CompanyFormModal
          open={createOpen}
          mode="create"
          onClose={() => {
            setCreateOpen(false);
            mutations.createCompany.reset();
          }}
          onSubmit={submitCreate}
          submitting={mutations.createCompany.isPending}
          submitError={mutations.createCompany.error}
        />
      )}

      {allowUpdate && (
        <>
          <CompanyFormModal
            open={editTarget !== null}
            mode="edit"
            company={editTarget}
            onClose={() => {
              setEditTarget(null);
              mutations.updateCompany.reset();
            }}
            onSubmit={submitEdit}
            submitting={mutations.updateCompany.isPending}
            submitError={mutations.updateCompany.error}
          />
          <CompanyArchiveModal
            open={archiveTarget !== null}
            company={archiveTarget}
            onClose={() => {
              setArchiveTarget(null);
              mutations.updateCompany.reset();
            }}
            onConfirm={submitArchive}
            submitting={mutations.updateCompany.isPending}
            submitError={mutations.updateCompany.error}
          />
        </>
      )}
    </section>
  );
}
